import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import { ILegalClientDocumentVersion } from '../../Interfaces/ILegalClientDocumentVersion';
import { DocumentInlinePreview } from './DocumentInlinePreview';
import { ImageZoomDialog } from './ImageZoomDialog';

interface IProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  /** Version, deren Dateien gezeigt werden. */
  version: ILegalClientDocumentVersion | undefined;
  typeName: string;
  /** Datei, mit der die Vorschau öffnen soll — sonst die erste. */
  initialIdDocument?: number;
}

/**
 * Vorschau einer kompletten Version. Eine Version kann aus mehreren Dateien
 * bestehen (Vorder-/Rückseite), deshalb sind hier **alle** Dateien der Version
 * über Reiter erreichbar und nicht nur die erste.
 */
export const DocumentVersionPreviewDialog: React.FC<IProps> = props => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [zoomUrl, setZoomUrl] = useState<string | null>(null);

  const fileArray = props.version?.FileArray ?? [];
  const selectedFile = fileArray[selectedIndex];

  useEffect(() => {
    if (!props.isOpen) {
      return;
    }

    const initialIndex = (props.version?.FileArray ?? []).findIndex(
      file => file.idDocument === props.initialIdDocument
    );

    setSelectedIndex(initialIndex < 0 ? 0 : initialIndex);
  }, [props.isOpen, props.version, props.initialIdDocument]);

  const fileLabel = (index: number): string => {
    const file = fileArray[index];

    return file.Label !== null && file.Label !== undefined && file.Label !== ''
      ? file.Label
      : file.FileName;
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => props.setIsOpen(false)}
      maxWidth='md'
      fullWidth
    >
      <DialogTitle>
        {props.typeName}
        <Typography variant='body2' color='text.secondary'>
          Version {props.version?.VersionNumber}
          {fileArray.length > 1 && ` — ${fileArray.length} Dateien`}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {fileArray.length > 1 && (
          <Tabs
            value={selectedIndex}
            onChange={(event: React.SyntheticEvent, value: number) =>
              setSelectedIndex(value)
            }
            variant='scrollable'
            scrollButtons='auto'
            sx={{ mb: 2 }}
          >
            {fileArray.map((file, index) => (
              <Tab
                key={`preview-${file.idDocument}`}
                label={fileLabel(index)}
              />
            ))}
          </Tabs>
        )}

        {selectedFile === undefined ? (
          <Alert severity='info'>Diese Version enthält keine Datei.</Alert>
        ) : (
          <DocumentInlinePreview
            idDocument={selectedFile.idDocument}
            fileName={selectedFile.FileName}
            height={600}
            onZoom={setZoomUrl}
            pendingData={selectedFile.Data}
            pendingIdMIMEType={selectedFile.idMIMEType}
          />
        )}
      </DialogContent>
      <ImageZoomDialog
        objectUrl={zoomUrl}
        onClose={() => setZoomUrl(null)}
        alt={selectedFile?.FileName ?? props.typeName}
      />

      <DialogActions>
        <Button variant='contained' onClick={() => props.setIsOpen(false)}>
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
};
